import isSimpleObject from "./isSimpleObject";

export enum ArrayMergeMethod {
	// Override each item by index. Undefined items are ignored but null are valid
	ByIndex,
	// Concat arrays
	Push,
	// Clear array before adding to it.
	ClearAndPush,
}

/**
 * This method merges the sources which are simple objects or arrays. Sources are processed in ascending order (0 to ...).
 * For simple objects, own and enumerable properties are copied. Other properties are ignored.
 * For arrays, all values are copied.
 *
 * The source objects are not modified. You can provide up to 10 source objects as entries in the sources array.
 * By default a new object is returned which is a deep merge of the source objects. If you want an object to be modified
 * use the "target" property of "options.
 *
 * Any sources that are `undefined` or `null` will be ignored. Properties on the sources which are `undefined` will
 * be ignored but `null` values will override. If ALL sources are `undefined` or `null`, an error will be thrown.
 *
 * The options include providing a "target" and "arrayMerging" modifying the default method to merge
 * arrays (see ArrayMergeMethod enum for options). If the target (or first source if target not provided) is an
 * array the merge type is ArrayMergeMethod.Push. Otherwise, ArrayMergeMethod.ClearAndPush is used.
 *
 * If no "target" is provided an object is created which is similar to the first not null or undefined source.
 * This means you can merge sources which are arrays and/or simple objects.
 *
 * If you have one object or array which you want to deep copy, use deepCopyObject()
 *
 * TODO Add a way to duplicate other types of objects? Make a registry. Right now when an object that is not "simple" or an array is ran into, it is assigned; and not copied. We could make it so you can registered to add support for duplicating other types.
 * TODO Consider an option which freezes any value which is not a copy (e.g. a Date object). And potentially a debug mode which outputs what could not be copied and was, if set to do so, frozen.
 */
export default function deepCopy<
	T extends object | null,
	S1 extends T, S2 extends T, S3 extends T, S4 extends T, S5 extends T,
	S6 extends T, S7 extends T, S8 extends T, S9 extends T, S10 extends T
>(
	sources: [S1, S2?, S3?, S4?, S5?, S6?, S7?, S8?, S9?, S10?],
	options?: {target?: object | [], arrayMerging?: ArrayMergeMethod}
): S1 & S2 & S3 & S4 & S5 & S6 & S7 & S8 & S9 & S10 {

	let target: any;
	if (options !== undefined && options.target !== undefined) {
		target = options.target;
	} else {
		// Create a target object that has the same proto as the first not null or undefined source
		const firstNotUndefined = sources.find(source => source !== undefined && source !== null);
		if (firstNotUndefined === undefined) {
			throw new Error(`All sources are undefined or null.`);
		}
		target = createSimilar(firstNotUndefined);
	}

	let arrayMerging = options?.arrayMerging ?? (Array.isArray(target) ? ArrayMergeMethod.Push : ArrayMergeMethod.ClearAndPush);

	sources.forEach(source => {
		if (source !== undefined && source !== null) {
			_copyToTarget(target, source, arrayMerging);
		}
	});

	return target as S1 & S2 & S3 & S4 & S5 & S6 & S7 & S8 & S9 & S10;
}

/**
 * Copy a single object.
 * @param source
 */
export function deepCopyObject<S1 extends object | []>(
	source: S1
): S1 {
	if (source === undefined || source === null) {
		return source;
	}
	const target = createSimilar(source);
	_copyToTarget(target, source, ArrayMergeMethod.ByIndex); // The method of array merge should not matter since we are only working with one source.

	return target;
}

function _copyToTarget(target: any, source: any, arrayMerging: ArrayMergeMethod): any {
	if (
		target === null || target === undefined
		|| source === null || source === undefined
	) {
		throw new Error(`Target and/or source are undefined or null.`);
	}

	let useArrayPush = false;
	if (Array.isArray(target)) {
		if (arrayMerging === ArrayMergeMethod.ClearAndPush) {
			target.length = 0; // Clear the array if not merging
		}
		if (arrayMerging >= ArrayMergeMethod.Push) {
			useArrayPush = true
		}
	}

	const properties = Object.keys(source);
	properties.forEach(key => {
		const sourceValue = source[key];
		if (sourceValue === null) {
			if (useArrayPush) {
				target.push(null);
			} else {
				target[key] = null;
			}
		} else if (sourceValue !== undefined) {
			if (isSimpleObject(sourceValue)) {
				if (useArrayPush) {
					target.push(
						_copyToTarget(createSimilar(sourceValue), sourceValue, arrayMerging)
					);
				} else {
					if (target[key] === undefined || target[key] === null || !isSimpleObject(target[key])) {
						target[key] = createSimilar(sourceValue);
					}
					_copyToTarget(target[key], sourceValue, arrayMerging);
				}
			} else {
				if (useArrayPush) {
					target.push(sourceValue);
				} else if (Array.isArray(target[key])) {
					_copyToTarget(target[key], sourceValue, arrayMerging);
				} else {
					target[key] = sourceValue;
				}
			}
		}
	});

	return target;
}



/**
 * Create a new object with the same prototype as the passed object or a new
 * array if the passed object is an array.
 */
function createSimilar<O extends any>(obj: O): O {
	return Array.isArray(obj) ? [] : Object.create(Object.getPrototypeOf(obj));
}
